import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWorkspace } from '../../../context/WorkspaceContext';
import workspaceApi from '../../../api/workspaceApi';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

// Specific colors for LLM usage to map events
const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#8B5CF6'];

const getUsageDisplayLabel = (eventType?: string) => {
    const normalized = (eventType || '').trim().toLowerCase().replace(/\s+/g, '_');

    switch (normalized) {
        case 'vendor_resolution':
            return 'Transaction Categorization';
        case 'llm_chat':
            return 'AI CFO Chat';
        case 'llm_background':
            return 'Background AI';
        case 'summary_generation':
            return 'Insights Summary';
        case 'doc_parse':
            return 'Document Extraction';
        case 'signal_generation':
            return 'Action Signals';
        case 'conversation_memory':
            return 'Chat Memory';
        case 'conversation_title':
            return 'Conversation Titles';
        case 'vector_memory_summary':
            return 'Vector Memory Summary';
        case 'cash_forecast_validation':
            return 'Cash Forecast Validation';
        case 'invoice_processing':
            return 'Invoice Processing';
        case 'qbo_account_mapping':
            return 'QBO Account Mapping';
        default:
            return (eventType || '').replace(/_/g, ' ');
    }
};

export default function Quota() {
    const { workspaces, activeWorkspaceId, setActiveWorkspaceId, isLoading: isWorkspaceLoading } = useWorkspace();
    const [daysFilter, setDaysFilter] = useState(30);

    // Fetch quota details
    const { data: quotaRes, isLoading: isQuotaLoading } = useQuery({
        queryKey: ['workspaceQuota', activeWorkspaceId],
        queryFn: () => workspaceApi.getWorkspaceQuota(),
        enabled: !!activeWorkspaceId,
    });

    // Fetch LLM Usage detail
    const { data: usageRes, isLoading: isUsageLoading } = useQuery({
        queryKey: ['workspaceLlmUsage', activeWorkspaceId, daysFilter],
        queryFn: () => workspaceApi.getWorkspaceLlmUsage(activeWorkspaceId!, daysFilter),
        enabled: !!activeWorkspaceId,
    });

    const entitlement = quotaRes?.data?.entitlement;
    const quota = quotaRes?.data?.quota;
    const usageData = usageRes?.data || [];
    const resolveUsageLabel = (eventType?: string, eventLabel?: string) => eventLabel || getUsageDisplayLabel(eventType);

    // Formatting Helpers
    const formatNumber = (num: number | undefined) => {
        if (num === undefined) return '0';
        return num.toLocaleString();
    };

    const calculatePercentage = (used: number, limit: number) => {
        if (!limit || limit === 0) return 0;
        const p = (used / limit) * 100;
        return Math.min(Math.round(p), 100);
    };

    if (isWorkspaceLoading) {
        return (
            <div className="flex justify-center items-center p-12">
                <Loader2 className="animate-spin text-gray-500 w-6 h-6" />
            </div>
        );
    }

    if (workspaces.length === 0) {
        return <div className="p-6 text-gray-500">No workspaces available.</div>;
    }

    return (
        <div className="w-full overflow-x-hidden py-2 pb-20 sm:py-6">
            <div className="w-full max-w-4xl font-medium">

                {/* header and selector */}
                <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Billing & Quota</h2>
                        <p className="text-sm text-gray-500 mt-1">Manage your usage and subscription</p>
                    </div>

                    <div className="flex w-full flex-col gap-1 sm:w-auto sm:items-end">
                        <label className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Active Workspace</label>
                        <div className="relative flex w-full min-w-0 items-center rounded-lg border bg-white shadow-sm sm:min-w-[220px]">
                            <select
                                value={activeWorkspaceId || ''}
                                onChange={(e) => setActiveWorkspaceId(e.target.value)}
                                className="w-full appearance-none rounded-lg border-none bg-white px-3 py-2 pr-10 text-sm font-medium leading-tight text-gray-700 focus:outline-none focus:ring-0 sm:px-4"
                            >
                                {workspaces.map((ws) => (
                                    <option key={ws.workspace_id} value={ws.workspace_id}>
                                        {ws.workspace_name || ws.workspace_id} ({ws.role})
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                                <ChevronDown className="h-4 w-4" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-stretch gap-4 sm:gap-6 lg:flex-row">
                    {/* Left Column: Plan & Limits */}
                    <div className="flex min-w-0 flex-1 flex-col gap-4 sm:gap-6">
                        {/* Plan Status Box */}
                        <div className="flex flex-col items-start justify-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
                            <div className="text-sm text-gray-500">Current Plan</div>
                            {isQuotaLoading ? (
                                <Loader2 className="animate-spin text-gray-300 w-5 h-5 mx-auto" />
                            ) : (
                                <>
                                    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-1 text-xl font-bold capitalize text-gray-900 sm:text-2xl">
                                        {getPlanLabel(entitlement?.plan_status)}
                                    </div>
                                    {entitlement?.plan_status === 'free' && entitlement?.trial_end_at && (
                                        <div className="text-sm text-gray-500 bg-orange-50 text-orange-600 px-3 border border-orange-200 py-1 rounded-full items-center gap-1 mt-1">
                                            Trial ends: {new Date(entitlement.trial_end_at).toLocaleDateString()}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Usage Progress Box */}
                        <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
                            <h3 className="mb-5 text-base font-semibold text-gray-900 sm:mb-6 sm:text-lg">Current Period Usage</h3>

                            {isQuotaLoading ? (
                                <div className="flex justify-center py-4">
                                    <Loader2 className="animate-spin text-gray-400 w-6 h-6" />
                                </div>
                            ) : (
                                <div className="space-y-5 sm:space-y-6">

                                    {/* LLM Tokens */}
                                    <div>
                                        <div className="mb-2 flex items-end justify-between gap-3">
                                            <span className="text-sm font-semibold text-gray-700">AI Usage</span>
                                            <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                                                {formatNumber(quota?.llm_tokens?.used)} / {formatNumber(quota?.llm_tokens?.limit)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                            <div
                                                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                                                style={{ width: `${calculatePercentage(quota?.llm_tokens?.used || 0, quota?.llm_tokens?.limit || 1)}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    {/* Document Units */}
                                    <div>
                                        <div className="mb-2 flex items-end justify-between gap-3">
                                            <span className="text-sm font-semibold text-gray-700">Documents Processed</span>
                                            <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                                                {formatNumber(quota?.doc_units?.used)} / {formatNumber(quota?.doc_units?.limit)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                            <div
                                                className="bg-emerald-500 h-2.5 rounded-full transition-all duration-500"
                                                style={{ width: `${calculatePercentage(quota?.doc_units?.used || 0, quota?.doc_units?.limit || 1)}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="mt-4 border-t pt-4 text-xs text-gray-400">
                                        Period: {new Date(quota?.period_start_at || '').toLocaleDateString()} — {new Date(quota?.period_end_at || '').toLocaleDateString()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: AI Tokens Breakdown */}
                    <div className="min-w-0 flex-1 rounded-2xl border bg-white p-4 shadow-sm sm:p-6">
                        <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
                            <h3 className="text-base font-semibold text-gray-900 sm:text-lg">AI Tokens Breakdown</h3>
                            <select
                                value={daysFilter}
                                onChange={(e) => setDaysFilter(Number(e.target.value))}
                                className="h-9 w-full rounded-md border-gray-300 py-1 pl-3 pr-8 text-sm text-gray-600 focus:ring-0 sm:w-auto"
                            >
                                <option value={7}>Last 7 Days</option>
                                <option value={30}>Last 30 Days</option>
                                <option value={90}>Last 90 Days</option>
                            </select>
                        </div>

                        {isUsageLoading ? (
                            <div className="flex justify-center flex-col items-center h-48">
                                <Loader2 className="animate-spin text-gray-300 w-8 h-8 mx-auto" />
                            </div>
                        ) : usageData.length === 0 ? (
                            <div className="flex justify-center items-center h-48 text-gray-400 text-sm">
                                No AI Tokens data in this period.
                            </div>
                        ) : (
                            <div>
                                <div className="h-56 w-full sm:h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={usageData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius="54%"
                                                outerRadius="82%"
                                                stroke="none"
                                                paddingAngle={2}
                                                dataKey="total_tokens"
                                                nameKey="event_type"
                                            >
                                                {usageData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value: number, name: string, item: any) => [
                                                    formatNumber(value) + ' tokens',
                                                    resolveUsageLabel(name, item?.payload?.event_label),
                                                ]}
                                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Legend manually crafted for better control */}
                                <div className="mt-4 grid max-h-48 grid-cols-1 gap-3 overflow-auto pr-1 sm:max-h-40 sm:grid-cols-2 sm:pr-2">
                                    {usageData.map((item, i) => (
                                        <div key={item.event_type} className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-xs font-semibold text-gray-700 truncate" title={resolveUsageLabel(item.event_type, item.event_label)}>
                                                    {resolveUsageLabel(item.event_type, item.event_label)}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {formatNumber(item.total_tokens)} Tokens
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
const getPlanLabel = (plan?: string | null) => {
    switch ((plan || '').toLowerCase()) {
        case 'standard':
            return 'Starter';
        case 'core':
        case 'pro':
        case 'paid':
            return 'Core';
        case 'free':
            return 'Free Trial';
        default:
            return plan || 'Unknown';
    }
};
